import { createApp } from 'vue'
import App from './App.vue'
import { createRouterInstance } from './router'
import pinia from './store'
import i18n, { initializeLanguage } from './locales'
// 关键CSS - 同步加载
import 'uno.css'
import './styles/main.scss'
import './styles/fonts.css'

// 设置初始标题
document.title = import.meta.env.VITE_APP_TITLE || ''

// 根据环境动态创建字体样式表
function createDynamicFontStyle() {
  const fontSource = __MFY_FONT_SOURCE__
  
  // 创建样式表
  const style = document.createElement('style')
  style.textContent = `
    @font-face {
      font-family: 'MFYuanHeiNoncommercial';
      src: url('${fontSource}') format('opentype');
      font-weight: normal;
      font-style: normal;
      font-display: swap;
    }
  `
  
  // 添加到head中
  document.head.appendChild(style)
  
  // 开发环境下输出字体配置信息用于测试
  if (import.meta.env.DEV) {
    console.log('🔤 字体环境配置信息:')
    console.log('当前环境:', __REGION__ === 'ww' ? '国际环境 (ww)' : '国内环境 (cn)')
    console.log('字体路径:', fontSource)
    console.log('动态样式表已创建')
  }
}

// 执行字体样式创建
createDynamicFontStyle()

// 异步加载非关键CSS文件
const loadNonCriticalCSS = async () => {
  try {
    // 并行加载所有非关键CSS（移除Vant全量CSS）
    await Promise.all([
      import('./styles/variable.css'),
      import('./styles/baseComponent.css'),
      import('./styles/typography.css'),
      import('./styles/theme.css'),
      import('./styles/reset.scss'),
      import('../public/iconfont/iconfont.css')
    ]);
    
    console.log('非关键CSS加载完成');
  } catch (error) {
    console.error('CSS加载失败:', error);
  }
};

// 异步加载Firebase（非关键）- 延迟3秒加载
const loadFirebase = () => {
  setTimeout(async () => {
    try {
      await import('./firebase');
      console.log('Firebase加载完成');
    } catch (error) {
      console.error('Firebase加载失败:', error);
    }
  }, 3000);
};

// 获取并缓存jhsapp.getEnvAddress()
localStorage.removeItem('jhs_env_address');
// try {
//   if (typeof window !== 'undefined' && window.jhsapp && typeof window.jhsapp.getEnvAddress === 'function') {
//     const envAddress = window.jhsapp.getEnvAddress();
//     console.log('init-envAddress', envAddress);
//     if (envAddress) {
//       localStorage.setItem('jhs_env_address', JSON.stringify(envAddress));
//     }
//   }
// } catch (error) {
//   console.error('获取jhsapp.getEnvAddress失败:', error);
// }

/**
 * 带重试机制获取环境地址
 */
const getEnvAddressWithRetry = (): Promise<void> => {
  console.log('getEnvAddressWithRetry 开始执行');
  return new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 4;
    const interval = 500;
    const initialDelay = 100; // 首次检查前等待100ms，给原生APP足够的注入时间

    const check = () => {
      try {
        if (typeof window !== 'undefined' && window.jhsapp && typeof window.jhsapp.getEnvAddress === 'function') {
          const envAddress = window.jhsapp.getEnvAddress();
          console.log('✅ 成功获取环境地址', envAddress);
          if (envAddress) {
            localStorage.setItem('jhs_env_address', JSON.stringify(envAddress));
            return resolve();
          }
        }
      } catch (error) {
        console.error('❌ 获取环境地址失败:', error);
      }

      // 重试逻辑
      attempts++;
      if (attempts < maxAttempts) {
        setTimeout(check, interval);
      } else {
        console.warn('⚠️ 获取环境地址失败: 重试次数耗尽');
        resolve();
      }
    };

    // 添加初始延迟后再开始第一次检查
    setTimeout(check, initialDelay);
  });
};

// 执行环境地址获取（不阻塞应用初始化）
getEnvAddressWithRetry();


if (import.meta.env.VITE_USE_VCONSOLE === 'true') {
  // @ts-ignore
  import('vconsole').then((module) => {
    // eslint-disable-next-line
    new module.default();
  });
}

const app = createApp(App)

// 忽略自定义元素标签抛出的报错
app.config.compilerOptions.isCustomElement = (tag) => {
  return ['wx-open-launch-app'].includes(tag)
}

app.use(pinia)
app.use(i18n)

// 初始化应用
const initApp = async () => {
  try {
    // 同时进行语言初始化、路由创建和非关键资源加载
    const [languageResult, routerResult] = await Promise.allSettled([
      initializeLanguage(),
      createRouterInstance(),
      loadNonCriticalCSS(),
      loadFirebase()
    ]);
    
    if (languageResult.status === 'rejected') {
      console.error('语言初始化失败:', languageResult.reason);
    }
    
    // 确保路由创建成功
    if (routerResult.status === 'fulfilled') {
      app.use(routerResult.value)
    } else {
      console.error('路由创建失败:', routerResult.reason);
      // 如果路由创建失败，尝试重新创建
      const fallbackRouter = await createRouterInstance();
      app.use(fallbackRouter);
    }
    
    app.mount('#app')
    
    console.log('应用初始化完成');
  } catch (error) {
    console.error('应用初始化失败:', error);
    // 确保应用能够启动，即使某些资源加载失败
    try {
      const router = await createRouterInstance();
      app.use(router);
      app.mount('#app');
    } catch (fallbackError) {
      console.error('应用启动失败:', fallbackError);
    }
  }
};

// 启动应用
initApp();